import type {HTMLAttributes, ReactNode} from 'react';

import {cn} from '../../../helpers/cn';
import {MobileLayout} from '../MobileLayout';

export type AppLayoutProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  header?: ReactNode;
  /** Omit for header-only pages (`hasNavigate: false`). */
  tabBar?: ReactNode;
  contentClassName?: string;
  /**
   * When false, the content slot does not scroll — use `PageLayout` / `DetailPageLayout` inside.
   * Shell routes (`shell/layout`) pass `false`.
   */
  contentScroll?: boolean;
};

/**
 * App shell — optional top header, scrollable content, optional bottom tab bar.
 * With `tabBar`: `hasHeader: true`, `hasNavigate: true` (tab roots).
 * Without `tabBar`: `hasHeader: true`, `hasNavigate: false` (detail/stack pages).
 */
export function AppLayout({
  children,
  header,
  tabBar,
  contentClassName,
  contentScroll = true,
  className,
  ...props
}: AppLayoutProps) {
  return (
    <MobileLayout className={cn('flex h-full min-h-0 flex-col', className)} {...props}>
      {header}
      <main
        className={cn(
          'flex-1 bg-gray-50',
          contentScroll ? 'overflow-y-auto' : 'flex min-h-0 flex-col overflow-hidden',
          contentClassName
        )}
      >
        {children}
      </main>
      {tabBar}
    </MobileLayout>
  );
}
